import { ContactAvatar } from '@poseidon/components/Avatar';
import { cn } from '@poseidon/components/cn';
import { Tooltip } from '@poseidon/components/Input2';

import { HoverPeoplePopover } from '~/components/HoverPeoplePopover';

/**
 * One enrolled contact positioned on the touch graph — the fields a face needs
 * to render an avatar. Shared by the canvas overlay and the count badge itself.
 */
export interface TouchFlowContact {
  cadenceId: string;
  contactId: string;
  firstName: string | null;
  lastName: string | null;
  imageSrc: string | null;
  orgImageSrc: string | null;
}

/** How many faces to show in the hover grid before collapsing the rest into a "+N" chip. */
const MAX_GRID_FACES = 24;

const contactName = (c: TouchFlowContact) => [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Unknown';

/**
 * A compact "N people here" count chip that tucks into the bottom-left corner of a
 * touch node. Hovering it reveals a floating grid of the enrolled contacts' faces
 * (a real popover with a solid background + shadow) instead of showing the faces
 * inline beside the card. Renders nothing when no contacts sit at this touch.
 */
export function ContactCountBadge({
  contacts,
  onContactClick,
}: {
  contacts: TouchFlowContact[];
  /** Open a contact's detail when its face is clicked. Omit to render faces non-interactive. */
  onContactClick?: (contact: TouchFlowContact) => void;
}) {
  if (contacts.length === 0) return null;

  const shown = contacts.slice(0, MAX_GRID_FACES);
  const overflow = contacts.length - shown.length;

  return (
    <HoverPeoplePopover
      panelClassName="w-[212px]"
      panelTestId="touch-contact-grid"
      trigger={
        // The always-visible count chip (number only) — the hover target.
        <div
          data-testid="touch-contact-count"
          className={cn(
            'inline-flex cursor-default items-center justify-center rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold shadow-sm ring-1',
            'text-lynch-600 ring-lynch-200 dark:bg-slate-900 dark:text-lynch-200 dark:ring-slate-700',
          )}>
          {contacts.length}
        </div>
      }>
      <div className="mb-1.5 px-0.5 text-[11px] font-semibold text-lynch-500 dark:text-lynch-300">
        {contacts.length} contact{contacts.length === 1 ? '' : 's'} here
      </div>
      <div className="flex flex-wrap gap-1">
        {shown.map((c) => (
          <Tooltip key={c.cadenceId} tooltip={contactName(c)}>
            <div
              role={onContactClick != null ? 'button' : undefined}
              tabIndex={onContactClick != null ? 0 : undefined}
              // Stop the click from bubbling to the card, which would also open the
              // step editor (onSelectStep). Enter/Space activate it for keyboard users.
              onClick={
                onContactClick != null
                  ? (e) => {
                      e.stopPropagation();
                      onContactClick(c);
                    }
                  : undefined
              }
              onKeyDown={
                onContactClick != null
                  ? (e) => {
                      if (e.key !== 'Enter' && e.key !== ' ') return;
                      e.preventDefault();
                      e.stopPropagation();
                      onContactClick(c);
                    }
                  : undefined
              }
              className={cn(
                'rounded-full',
                onContactClick != null &&
                  'cursor-pointer outline-none transition hover:ring-2 hover:ring-blue-400 focus-visible:ring-2 focus-visible:ring-blue-400',
              )}>
              <ContactAvatar
                firstName={c.firstName}
                lastName={c.lastName}
                imageSrc={c.imageSrc}
                orgImageSrc={c.orgImageSrc}
                className="size-7"
              />
            </div>
          </Tooltip>
        ))}
        {overflow > 0 && (
          <div
            data-testid="touch-contact-grid-overflow"
            className="flex size-7 items-center justify-center rounded-full bg-lynch-100 text-[10px] font-semibold text-lynch-600 dark:bg-slate-700 dark:text-lynch-200">
            +{overflow}
          </div>
        )}
      </div>
    </HoverPeoplePopover>
  );
}
